/**
 * Window-level OOM detection.
 *
 * WASM out-of-memory traps can bubble up through `window.onerror` or
 * `unhandledrejection` before React can render an error screen - on mobile
 * Safari the tab may be terminated outright. We can't guarantee catching it
 * in that case, but when we do we (a) emit an `oom.detected` Mixpanel event
 * so we can track real-world hit rates, and (b) on mobile set a session flag
 * so the next load attempt on this tab can short-circuit to the mobile
 * fallback screen.
 */

import { events } from '@/app/events/events';
import { MOBILE_OOM_SESSION_FLAG } from '@/app/helpers/fileSizeLimits';
import { isMobileMode } from '@/app/helpers/isMobile';
import { isOutOfMemoryError } from '@/shared/utils/isOutOfMemoryError';
import { trackOomDetected, type OomSurface } from '@/shared/utils/oomAnalytics';

let installed = false;

const markOom = (surface: OomSurface, error: unknown, source?: string) => {
  const isMobile = isMobileMode();
  trackOomDetected({ surface, source, isMobile, error });
  if (isMobile) {
    try {
      if (typeof window === 'undefined') return;
      window.sessionStorage?.setItem(MOBILE_OOM_SESSION_FLAG, 'true');
    } catch {
      // sessionStorage may be disabled (e.g. Safari private mode); best-effort.
    }
  }
};

export const installMobileOomHandler = (): void => {
  if (installed) return;
  if (typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    const err = event.error ?? event.message;
    if (isOutOfMemoryError(err)) {
      markOom('window.error', err);
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (isOutOfMemoryError(event.reason)) {
      markOom('window.rejection', event.reason);
    }
  });

  // Single point of truth for `coreError` -> OOM tracking. Installed at app
  // init so it fires even when OOM happens before any React screen renders
  // (e.g. crash during initial file load, before QuadraticUI mounts).
  events.on('coreError', (source: string, error: Error | unknown) => {
    if (isOutOfMemoryError(error)) {
      markOom('coreError', error, source);
    }
  });
};
